import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, addDays, addMinutes, startOfToday, differenceInMinutes } from 'date-fns';
import { apiClient } from '../api/client';
import { analytics } from '../hooks/useAnalytics';
import { useTranslation } from '../i18n';

// ─── styles ───────────────────────────────────────────────────────────────────

const errorShakeStyle = `
  @keyframes errorShake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-4px); }
    75% { transform: translateX(4px); }
  }
  .error-shake {
    animation: errorShake 0.4s ease-in-out 3;
  }
`;

const styleSheet = new CSSStyleSheet();
try {
  styleSheet.replaceSync(errorShakeStyle);
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, styleSheet];
} catch {
  // Fallback for browsers that don't support adoptedStyleSheets
  const style = document.createElement('style');
  style.textContent = errorShakeStyle;
  document.head.appendChild(style);
}

// ─── types ────────────────────────────────────────────────────────────────────

type QuickOption = 'today18' | 'today22' | 'tomorrow12' | 'custom';

interface UntilCard {
  id: QuickOption;
  dayLabel?: string;
  timeLabel?: string;
  isCustom?: boolean;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function todayAt(h: number, m = 0) {
  const d = startOfToday();
  d.setHours(h, m, 0, 0);
  return d;
}

function tomorrowAt(h: number, m = 0) {
  const d = addDays(startOfToday(), 1);
  d.setHours(h, m, 0, 0);
  return d;
}

function fromDatetime(date: string, time: string): Date {
  return new Date(`${date}T${time}:00`);
}

function formatPreview(d: Date) {
  return format(d, 'EEE d MMM, HH:mm');
}

function formatDuration(from: Date, to: Date) {
  const mins = differenceInMinutes(to, from);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}min` : `${h}h`;
}

/** Today's date string in yyyy-MM-dd (browser local) */
function todayStr() { return format(new Date(), 'yyyy-MM-dd'); }
/** Current time string HH:mm */
function nowTimeStr() { return format(new Date(), 'HH:mm'); }
/** Current time + N minutes as HH:mm */
function nowPlusMinStr(n: number) { return format(addMinutes(new Date(), n), 'HH:mm'); }

// ─── MarkFreePage ─────────────────────────────────────────────────────────────

export default function MarkFreePage() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc       = useQueryClient();
  const { t, language } = useTranslation();

  // Free from
  const [fromNow,        setFromNow]        = useState(true);
  const [customFromDate, setCustomFromDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [customFromTime, setCustomFromTime] = useState(format(new Date(), 'HH:mm'));

  // Free until
  const [quickOption,  setQuickOption]  = useState<QuickOption>('today18');
  const [customToDate, setCustomToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [customToTime, setCustomToTime] = useState(format(addMinutes(new Date(), 60), 'HH:mm'));

  const [note,     setNote]     = useState('');
  const [apiError, setApiError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  // Funnel start — user opened the Mark Free page for this spot
  useEffect(() => {
    if (id) analytics.trackMarkFreeStarted(id);
  }, [id]);

  useEffect(() => {
    if (apiError && errorRef.current) {
      // Add shake animation
      errorRef.current.classList.add('error-shake');

      // Scroll into view
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Remove shake class after animation completes (0.4s * 3 = 1.2s)
      const shakeTimer = setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.classList.remove('error-shake');
        }
      }, 1200);

      // Auto-dismiss after 5 seconds
      const dismissTimer = setTimeout(() => {
        setApiError(null);
      }, 5000);

      return () => {
        clearTimeout(shakeTimer);
        clearTimeout(dismissTimer);
      };
    }
  }, [apiError]);

  const { data: spotData } = useQuery({
    queryKey: ['spot', id],
    queryFn:  () =>
      apiClient
        .get<{ data: { spot_number: string; level_code: string; level_label: string } }>(`/spots/${id}`)
        .then((r) => r.data.data),
    enabled: !!id,
  });

  const spotLabel = spotData ? `${t(spotData.level_code) || spotData.level_label} · ${spotData.spot_number}` : '…';

  const startsAt = useMemo<Date>(() => {
    if (fromNow) return new Date();
    return fromDatetime(customFromDate, customFromTime);
  }, [fromNow, customFromDate, customFromTime]);

  const endsAt = useMemo<Date>(() => {
    switch (quickOption) {
      case 'today18':    return todayAt(18);
      case 'today22':    return todayAt(22);
      case 'tomorrow12': return tomorrowAt(12);
      case 'custom':     return fromDatetime(customToDate, customToTime);
    }
  }, [quickOption, customToDate, customToTime]);

  const isValid  = endsAt > startsAt && endsAt > new Date();
  const duration = isValid ? formatDuration(startsAt, endsAt) : null;

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/spots/${id}/windows`, {
        type:      'free',
        starts_at: startsAt.toISOString(),
        ends_at:   endsAt.toISOString(),
        note:      note.trim() || undefined,
      }),
    onSuccess: async () => {
      const durationMinutes = Math.floor(differenceInMinutes(endsAt, startsAt));
      analytics.trackSpotMarkedFree(id!, durationMinutes);
      analytics.trackAvailabilityWindowCreated('free', durationMinutes);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['spot', id] }),
        qc.invalidateQueries({ queryKey: ['spots', 'mine'] }),
      ]);
      navigate(`/spots/${id}`);
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })
        ?.response?.data?.error?.code;
      if (code === 'WINDOW_OVERLAP') setApiError('Overlaps an existing free window');
      else if (code === 'ENDS_IN_PAST') setApiError('End time must be in the future');
      else setApiError('Something went wrong. Please try again.');
      analytics.trackError('MARK_FREE_FAILED', code);
    },
  });

  const untilCards: UntilCard[] = [
    { id: 'today18',    dayLabel: t('today'),    timeLabel: format(todayAt(18), 'HH:mm') },
    { id: 'today22',    dayLabel: t('today'),    timeLabel: format(todayAt(22), 'HH:mm') },
    { id: 'tomorrow12', dayLabel: t('tomorrow'), timeLabel: format(tomorrowAt(12), 'HH:mm') },
    { id: 'custom',     isCustom: true },
  ];

  // Check if each preset is in the past
  const isPastTime = (time: Date) => time < new Date();

  return (
    <div className="page" style={{ maxWidth: '430px', margin: '0 auto', fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>

      {/* ── Top bar ── matches SpotDetailPage / tab pages: .topbar class
           owns the safe-area padding-top, position:sticky keeps it in the
           flex flow so the body never needs to compensate. */}
      <header className="topbar" style={{
        position:             'sticky',
        top:                  0,
        zIndex:               50,
        backgroundColor:      'rgba(248,249,250,0.85)',
        backdropFilter:       'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow:            '0 12px 32px -4px rgba(43,52,55,0.06)',
      }}>
        {/* X close button */}
        <button
          onClick={() => navigate(-1)}
          style={{
            width:          '40px',
            height:         '40px',
            border:         'none',
            background:     'none',
            cursor:         'pointer',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            borderRadius:   '9999px',
            color:          '#005bc0',
            flexShrink:     0,
          }}
          aria-label="Close"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>close</span>
        </button>

        {/* SVG logo — absolutely centered so it's never pushed off by button width */}
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 114 26"
            style={{ height: '26px', width: 'auto', display: 'block' }}
            aria-label="Lot'N'Go"
          >
            <text
              x="57"
              y="20"
              fontFamily="Manrope, system-ui, sans-serif"
              fontWeight="700"
              fontSize="20"
              fill="#005bc0"
              textAnchor="middle"
              letterSpacing="-0.4"
            >
              {"Lot'N'Go"}
            </text>
          </svg>
        </div>

        {/* Right spacer matches X button so logo stays truly centred */}
        <div style={{ width: '40px', flexShrink: 0 }} />
      </header>

      {/* ── Scrollable body ── header + bottom bar are both in flex flow,
           so a plain constant padding is enough; no env() math needed. */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px 24px' }}>

        {/* Spot info card */}
        <div style={{
          background:   '#ffffff',
          borderRadius: '20px',
          padding:      '20px 22px',
          boxShadow:    '0 8px 24px -4px rgba(43,52,55,0.06)',
          marginBottom: '28px',
        }}>
          <span style={{
            fontSize:      '10px',
            fontWeight:    700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color:         '#005bc0',
            display:       'block',
            marginBottom:  '6px',
          }}>
            {t('active_spot')}
          </span>
          <h2 style={{
            fontFamily:   "'Manrope', system-ui, sans-serif",
            fontSize:     '26px',
            fontWeight:   800,
            color:        '#2b3437',
            margin:       0,
            lineHeight:   1.2,
            letterSpacing:'-0.02em',
          }}>
            {spotLabel}
          </h2>
        </div>

        {/* ── Free from ── */}
        <section style={{ marginBottom: '28px' }}>
          <h3 style={sectionHeading}>{t('free_from')}</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {/* Now */}
            <button
              onClick={() => setFromNow(true)}
              style={{ ...fromBtn, ...(fromNow ? fromBtnActive : {}) }}
            >
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: '20px',
                  fontVariationSettings: fromNow
                    ? "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24"
                    : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
                }}
              >
                bolt
              </span>
              {t('now')}
            </button>

            {/* Pick time */}
            <button
              onClick={() => setFromNow(false)}
              style={{ ...fromBtn, ...(!fromNow ? fromBtnActive : {}) }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>schedule</span>
              {t('pick_time')}
            </button>
          </div>

          {/* Pick time inputs — date min today, time min now when today */}
          {!fromNow && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              {/* lang= forces the native picker to match the UI language
                  instead of the device's system locale. Without this, an
                  English-UI user on a Hebrew-locale phone sees Hebrew
                  month/day names in the picker. */}
              <input
                type="date"
                lang={language}
                value={customFromDate}
                min={todayStr()}
                onChange={(e) => setCustomFromDate(e.target.value)}
                style={inputStyle}
              />
              <input
                type="time"
                lang={language}
                value={customFromTime}
                min={customFromDate === todayStr() ? nowTimeStr() : undefined}
                onChange={(e) => setCustomFromTime(e.target.value)}
                style={inputStyle}
              />
            </div>
          )}
        </section>

        {/* ── Free until ── */}
        <section style={{ marginBottom: '28px' }}>
          <h3 style={sectionHeading}>{t('free_until')}</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {untilCards.map((card) => {
              const active = quickOption === card.id;
              let cardTime: Date | null = null;
              if (card.id === 'today18') cardTime = todayAt(18);
              else if (card.id === 'today22') cardTime = todayAt(22);
              else if (card.id === 'tomorrow12') cardTime = tomorrowAt(12);

              const isPast = cardTime && isPastTime(cardTime);
              const isDisabled = (isPast && !card.isCustom) || false;

              return (
                <button
                  key={card.id}
                  onClick={() => !isDisabled && setQuickOption(card.id)}
                  disabled={isDisabled}
                  style={{
                    display:        'flex',
                    flexDirection:  'column',
                    alignItems:     card.isCustom ? 'center' : 'flex-start',
                    justifyContent: card.isCustom ? 'center' : 'flex-end',
                    padding:        '14px 16px',
                    borderRadius:   '16px',
                    border:         active && !isDisabled ? '2px solid rgba(0,91,192,0.2)' : '1px solid transparent',
                    background:     isDisabled ? '#f5f5f5' : active ? '#dde8ff' : '#ffffff',
                    boxShadow:      active && !isDisabled ? 'none' : '0 4px 12px rgba(43,52,55,0.06)',
                    cursor:         isDisabled ? 'not-allowed' : 'pointer',
                    minHeight:      '80px',
                    transition:     'background 0.15s, border-color 0.15s',
                    opacity:        isDisabled ? 0.5 : 1,
                  }}
                >
                  {card.isCustom ? (
                    <>
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: '22px', color: active && !isDisabled ? '#005bc0' : '#586064', marginBottom: '4px' }}
                      >
                        edit_calendar
                      </span>
                      <span style={{
                        fontSize:      '11px',
                        fontWeight:    700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color:         active && !isDisabled ? '#005bc0' : '#586064',
                      }}>
                        {t('custom')}
                      </span>
                    </>
                  ) : (
                    <>
                      <span style={{
                        fontSize:      '10px',
                        fontWeight:    700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color:         isDisabled ? '#b3b3b3' : active ? '#005bc0' : '#586064',
                        marginBottom:  '6px',
                        display:       'block',
                      }}>
                        {card.dayLabel}
                      </span>
                      <span style={{
                        fontFamily: "'Manrope', system-ui, sans-serif",
                        fontSize:   '20px',
                        fontWeight: 700,
                        color:      isDisabled ? '#b3b3b3' : active ? '#005bc0' : '#2b3437',
                        lineHeight: 1,
                      }}>
                        {card.timeLabel}
                      </span>
                    </>
                  )}
                </button>
              );
            })}
          </div>

          {/* Custom pickers — date min today, time min now+5min when today */}
          {quickOption === 'custom' && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <input
                type="date"
                lang={language}
                value={customToDate}
                min={todayStr()}
                onChange={(e) => setCustomToDate(e.target.value)}
                style={inputStyle}
              />
              <input
                type="time"
                lang={language}
                value={customToTime}
                min={customToDate === todayStr() ? nowPlusMinStr(5) : undefined}
                onChange={(e) => setCustomToTime(e.target.value)}
                style={inputStyle}
              />
            </div>
          )}
        </section>

        {/* ── Optional note ── */}
        <section style={{ marginBottom: '24px' }}>
          <h3 style={sectionHeading}>{t('optional_note')}</h3>
          <div style={{ position: 'relative' }}>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('parking_note_placeholder')}
              rows={4}
              style={{
                width:        '100%',
                padding:      '16px',
                borderRadius: '16px',
                border:       'none',
                background:   '#ffffff',
                boxShadow:    '0 8px 24px -4px rgba(43,52,55,0.06)',
                fontSize:     '14px',
                fontFamily:   "'Inter', system-ui, sans-serif",
                color:        '#2b3437',
                resize:       'none',
                boxSizing:    'border-box',
                outline:      'none',
              }}
            />
            <div style={{
              position:      'absolute',
              bottom:        '12px',
              right:         '14px',
              fontSize:      '10px',
              fontWeight:    700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color:         '#c5cbd0',
              pointerEvents: 'none',
            }}>
              {t('parking_note')}
            </div>
          </div>
        </section>

        {/* ── Preview ── */}
        {isValid && duration && (
          <div style={{
            marginBottom: '16px',
            padding:      '14px 16px',
            borderRadius: '14px',
            background:   '#dde8ff',
          }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#005bc0', marginBottom: '2px' }}>
              Free {fromNow ? 'now' : formatPreview(startsAt)} → {formatPreview(endsAt)}
            </div>
            <div style={{ fontSize: '13px', color: '#005bc0' }}>({duration})</div>
          </div>
        )}

        {!isValid && (
          <div style={{ marginBottom: '16px', fontSize: '13px', color: '#dc2626' }}>
            {t('invalid_time_range')}
          </div>
        )}

        {/* ── Info card ── */}
        <div style={{
          display:      'flex',
          alignItems:   'flex-start',
          gap:          '12px',
          padding:      '14px 16px',
          borderRadius: '14px',
          background:   'rgba(226,226,233,0.4)',
        }}>
          <span
            className="material-symbols-outlined"
            style={{ color: '#005bc0', fontSize: '20px', flexShrink: 0, marginTop: '1px' }}
          >
            info
          </span>
          <p style={{ fontSize: '12px', fontWeight: 500, color: '#505157', lineHeight: 1.55, margin: 0 }}>
            {t('release_spot_info')}
          </p>
        </div>

        {/* API error */}
        {apiError && (
          <div
            ref={errorRef}
            style={{
              marginTop:    '16px',
              padding:      '10px 14px',
              borderRadius: '10px',
              background:   '#fef2f2',
              color:        '#dc2626',
              fontSize:     '13px',
            }}
          >
            {apiError}
          </div>
        )}
      </div>

      {/* ── Bottom: Release Spot button ── in-flow flex child (last in
           the .page column), so it stays at the bottom without
           position:fixed. padding-bottom uses max(env, 16px) so it
           clears the Samsung gesture/3-button bar on Android and the
           iOS home indicator on iOS. */}
      <div style={{
        flexShrink:           0,
        padding:              '16px 20px max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 16px)',
        background:           'rgba(248,249,250,0.88)',
        backdropFilter:       'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow:            '0 -8px 24px rgba(43,52,55,0.06)',
        boxSizing:            'border-box',
      }}>
        <button
          onClick={() => { setApiError(null); mutation.mutate(); }}
          disabled={!isValid || mutation.isPending}
          style={{
            display:       'block',
            width:         '100%',
            height:        '56px',
            borderRadius:  '16px',
            border:        'none',
            background:    (!isValid || mutation.isPending)
              ? '#c7d5e8'
              : 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
            color:         '#f7f7ff',
            fontSize:      '17px',
            fontWeight:    700,
            fontFamily:    "'Manrope', system-ui, sans-serif",
            letterSpacing: '-0.01em',
            cursor:        (!isValid || mutation.isPending) ? 'not-allowed' : 'pointer',
            boxShadow:     (!isValid || mutation.isPending)
              ? 'none'
              : '0 12px 32px -4px rgba(0,91,192,0.3)',
            transition:    'background 0.2s, box-shadow 0.2s',
          }}
        >
          {mutation.isPending ? t('saving') : t('release_spot')}
        </button>
      </div>
    </div>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const sectionHeading: React.CSSProperties = {
  fontSize:      '11px',
  fontWeight:    700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color:         '#586064',
  margin:        '0 0 12px',
};

const fromBtn: React.CSSProperties = {
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  gap:            '8px',
  padding:        '14px',
  borderRadius:   '14px',
  border:         '1px solid rgba(43,52,55,0.1)',
  background:     '#ffffff',
  color:          '#586064',
  fontSize:       '15px',
  fontWeight:     600,
  cursor:         'pointer',
  boxShadow:      '0 2px 8px rgba(43,52,55,0.05)',
  transition:     'background 0.15s, color 0.15s',
};

const fromBtnActive: React.CSSProperties = {
  background:  '#dde8ff',
  color:       '#005bc0',
  border:      '2px solid rgba(0,91,192,0.2)',
  boxShadow:   'none',
};

const inputStyle: React.CSSProperties = {
  flex:        1,
  height:      '44px',
  borderRadius:'10px',
  border:      '1px solid rgba(43,52,55,0.12)',
  padding:     '0 10px',
  fontSize:    '14px',
  background:  '#ffffff',
  boxSizing:   'border-box',
  color:       '#2b3437',
};
